<template>
  <section class="hmrag-wiki-shell">
    <aside class="wiki-sidebar">
      <div class="wiki-brand">
        <v-icon color="primary">library_books</v-icon>
        <strong>Healthmark Knowledge Wiki</strong>
      </div>

      <v-text-field
        v-model="search"
        prepend-inner-icon="search"
        label="Search products"
        clearable
        solo
        hide-details
        class="wiki-search"
      />

      <nav aria-label="Knowledge wiki products">
        <section v-for="group in filteredGroups" :key="group.category" class="wiki-group">
          <button
            type="button"
            class="wiki-category"
            :aria-expanded="isExpanded(group.category) ? 'true' : 'false'"
            @click="toggleCategory(group.category)"
          >
            <span>{{ isExpanded(group.category) ? '▾' : '▸' }}</span>
            {{ group.category || 'Other' }}
          </button>
          <div v-show="isExpanded(group.category)" class="wiki-products">
            <button
              v-for="page in group.pages"
              :key="page.slug"
              type="button"
              :class="['wiki-product', selectedPage && selectedPage.slug === page.slug ? 'active' : '']"
              @click="openPage(page)"
            >{{ page.name }}</button>
          </div>
        </section>
      </nav>
    </aside>

    <main ref="content" class="wiki-content">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="wiki-progress" />

      <v-alert v-if="error" :value="true" type="error" outline>{{ error }}</v-alert>

      <div v-else-if="!selectedPage" class="wiki-welcome">
        <h1>Healthmark Knowledge Wiki</h1>
        <p><strong>AI-compiled product information</strong> from official Healthmark documents.</p>
        <p>The official IFU remains the source of truth. Select a product from the library to begin.</p>
      </div>

      <template v-else-if="pageData">
        <div v-if="provenance.text" :class="['provenance', provenance.warning ? 'warning' : '']">
          <v-icon size="16">{{ provenance.warning ? 'warning' : 'verified_user' }}</v-icon>
          {{ provenance.text }}
        </div>
        <article class="wiki-article" v-html="safePageHtml" @click="handleArticleClick" />
      </template>
    </main>

    <ctlHmRagEvidencePopup ref="evidence" />
  </section>
</template>

<script>
import hmRagService from '@/services/hmRag.service'
import { sanitizeHmRagHtml } from '@/services/hmRagContent'
import ctlHmRagEvidencePopup from '@/components/ctlHmRagEvidencePopup'

export default {
  components: { ctlHmRagEvidencePopup },
  data: () => ({
    groups: [],
    expandedCategory: null,
    search: '',
    loading: false,
    error: '',
    selectedPage: null,
    pageData: null,
    // Object urls we minted for this page. Held so they can be released; a blob url keeps its
    // blob alive until it is revoked, so leaving them behind leaks a photo per page viewed.
    imageObjectUrls: []
  }),
  computed: {
    filteredGroups() {
      const query = (this.search || '').trim().toLowerCase()
      return this.groups.map(group => ({
        category: group.category,
        pages: (group.pages || []).filter(page => !query || page.name.toLowerCase().includes(query))
      })).filter(group => group.pages.length)
    },
    safePageHtml() {
      const slug = this.selectedPage && this.selectedPage.slug
      return sanitizeHmRagHtml(
        (this.pageData && this.pageData.html) || '',
        slug ? (reference => hmRagService.wikiImageUrl(slug, reference)) : null
      )
    },
    provenance() {
      const data = this.pageData || {}
      if (!data.is_admin) return { warning: false, text: '' }
      const meta = data.meta || {}
      const status = data.status || {}
      const compiled = meta.compiled_at ? ` · compiled ${meta.compiled_at.slice(0, 10)}` : ''
      if (status.freshness_status === 'stale' || status.freshness_status === 'blocked') {
        return {
          warning: true,
          text: `Stale — may be out of date. This page has not been re-approved since sources changed.${compiled}`
        }
      }
      if (status.review_status === 'approved' && status.freshness_status === 'current') {
        return { warning: false, text: `Approved${compiled}` }
      }
      return {
        warning: true,
        text: `AI-generated draft${status.review_status ? ` · status: ${status.review_status}` : ''}${compiled}`
      }
    }
  },
  created() {
    this.loadIndex()
  },
  beforeDestroy() {
    this.releaseImageObjectUrls()
  },
  methods: {
    async loadIndex() {
      this.loading = true
      this.error = ''
      try {
        const response = await hmRagService.getWikiIndex()
        this.groups = this.normalizeGroups(response.data || {})
        // A chat answer can link straight to one product page. The slug is matched against the
        // index we just loaded rather than trusted: openPage needs the page object, and an
        // unknown slug should simply leave the index showing instead of erroring.
        const wanted = this.$route.query.slug
        if (wanted) {
          const match = this.groups
            .reduce((all, group) => all.concat(group.pages || []), [])
            .find(page => page.slug === wanted)
          if (match) this.openPage(match)
        }
      } catch (error) {
        this.error = this.errorMessage(error, 'The knowledge library is temporarily unavailable.')
      } finally {
        this.loading = false
      }
    },
    normalizeGroups(data) {
      if (Array.isArray(data.groups)) return data.groups
      const pages = Array.isArray(data.pages) ? data.pages : []
      const grouped = {}
      pages.forEach(page => {
        const category = page.category || 'Other'
        if (!grouped[category]) grouped[category] = []
        grouped[category].push(page)
      })
      return Object.keys(grouped).sort().map(category => ({ category, pages: grouped[category] }))
    },
    isExpanded(category) {
      return this.expandedCategory === category
    },
    toggleCategory(category) {
      this.expandedCategory = this.isExpanded(category) ? null : category
    },
    async openPage(page) {
      this.selectedPage = page
      this.pageData = null
      this.loading = true
      this.error = ''
      this.$refs.evidence.close()
      try {
        const response = await hmRagService.getWikiPage(page.slug)
        this.pageData = response.data || {}
        this.$nextTick(() => {
          if (this.$refs.content) this.$refs.content.scrollTop = 0
          window.scrollTo(0, 0)
          this.loadPageImages()
        })
      } catch (error) {
        this.error = this.errorMessage(error, 'This wiki page is unavailable.')
      } finally {
        this.loading = false
      }
    },
    // v-html renders the <img> with no src, so nothing is requested until this runs. Each image
    // is fetched with the session's credentials and swapped in as an object url.
    async loadPageImages() {
      this.releaseImageObjectUrls()
      const root = this.$refs.content
      if (!root) return

      const images = Array.prototype.slice.call(root.querySelectorAll('img[data-hmrag-src]'))
      await Promise.all(images.map(async image => {
        const url = image.getAttribute('data-hmrag-src')
        image.removeAttribute('data-hmrag-src')
        try {
          const response = await hmRagService.getWikiImage(url)
          // The page may have moved on while this was in flight; a late swap would put one
          // product's photo on another product's page.
          // document.contains rather than node.isConnected: this app still supports IE11.
          if (!this.$refs.content || !document.body.contains(image)) return
          const objectUrl = window.URL.createObjectURL(response.data)
          this.imageObjectUrls.push(objectUrl)
          image.setAttribute('src', objectUrl)
        } catch (error) {
          // An image is decorative. A product without one, or a reference HmRag declines to
          // serve, must not disturb the page it illustrates.
          if (image.parentNode) image.parentNode.removeChild(image)
        }
      }))
    },

    releaseImageObjectUrls() {
      this.imageObjectUrls.forEach(url => window.URL.revokeObjectURL(url))
      this.imageObjectUrls = []
    },

    async handleArticleClick(event) {
      const anchor = event.target.closest && event.target.closest('a.evi')
      if (!anchor) return
      event.preventDefault()
      const claimId = anchor.getAttribute('data-claim-id')
      const citationNumber = Number(anchor.getAttribute('data-citation-n'))
      if (!this.selectedPage || !claimId || !citationNumber) return

      this.$refs.evidence.open(anchor, { citationNumber, loading: true })
      try {
        const response = await hmRagService.getWikiEvidence(
          this.selectedPage.slug,
          claimId,
          citationNumber
        )
        this.$refs.evidence.setEvidence((response.data && response.data.quotes) || [])
      } catch (error) {
        this.$refs.evidence.setError('Could not load the source. Please try again.')
      }
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      return (data && data.error) || fallback
    }
  }
}
</script>

<style scoped>
.hmrag-wiki-shell { background: #fff; border: 1px solid #e3e8ee; display: grid; grid-template-columns: 250px minmax(0, 1fr); min-height: 650px; }
.wiki-sidebar { align-self: start; background: #f7f8fa; border-right: 1px solid #e3e8ee; height: calc(100vh - 95px); overflow-y: auto; padding: 18px 13px; position: sticky; top: 75px; }
.wiki-brand { align-items: center; color: #1c2530; display: flex; gap: 8px; line-height: 1.3; margin: 0 5px 16px; }
.wiki-search { margin: 0 0 15px; }
.wiki-search >>> .v-input__slot { min-height: 42px; }
.wiki-group { border-top: 1px solid #e1e5e9; padding: 8px 0; }
.wiki-category { align-items: baseline; background: transparent; border: 0; color: #1c2530; cursor: pointer; display: flex; font-weight: 600; gap: 5px; padding: 5px 6px; text-align: left; width: 100%; }
.wiki-products { padding: 2px 0 3px; }
.wiki-product { background: transparent; border: 0; border-radius: 3px; color: #3867a6; cursor: pointer; display: block; line-height: 1.35; padding: 6px 11px 6px 19px; text-align: left; width: 100%; }
.wiki-product:hover { background: #edf2f9; }
.wiki-product.active { background: #e3ecfa; color: #174f9f; font-weight: 600; }
.wiki-content { min-width: 0; padding: 22px 30px 50px; position: relative; }
.wiki-progress { left: 0; margin: 0; position: absolute; right: 0; top: 0; }
.wiki-welcome { color: #44515e; margin: 40px auto; max-width: 700px; }
.wiki-welcome h1 { color: #1c2530; font-size: 29px; margin-bottom: 22px; }
.provenance { align-items: center; background: #eef8f0; border-radius: 6px; color: #32703f; display: inline-flex; font-size: 13px; gap: 5px; margin-bottom: 20px; padding: 6px 10px; }
.provenance.warning { background: #fff4e5; color: #8a6d0b; }
.wiki-article { color: #263442; font-size: 15px; line-height: 1.6; max-width: 1000px; }
.wiki-article >>> h1 { color: #1c2530; font-size: 30px; margin: 8px 0 22px; }
.wiki-article >>> h2 { color: #1c2530; font-size: 22px; margin: 27px 0 11px; }
.wiki-article >>> h3 { color: #1c2530; font-size: 17px; margin: 21px 0 8px; }
.wiki-article >>> p { margin: 0 0 13px; }
.wiki-article >>> ol, .wiki-article >>> ul { margin: 9px 0 15px; padding-left: 25px; }
.wiki-article >>> li { margin: 5px 0; }
.wiki-article >>> table { border-collapse: collapse; display: block; margin: 15px 0; max-width: 100%; overflow-x: auto; }
.wiki-article >>> td, .wiki-article >>> th { border: 1px solid #d9dde2; padding: 7px 10px; }
.wiki-article >>> th { background: #f3f5f7; }
.wiki-article >>> blockquote { background: #f6f8fb; border-left: 3px solid #1558d6; margin: 12px 0 19px; padding: 9px 13px; }
.wiki-article >>> a { color: #1558d6; }
.wiki-article >>> a.evi { font-weight: 600; text-decoration: none; }
.wiki-article >>> a.evi:hover { text-decoration: underline; }
@media (max-width: 800px) {
  .hmrag-wiki-shell { grid-template-columns: 1fr; }
  .wiki-sidebar { border-bottom: 1px solid #e3e8ee; border-right: 0; height: auto; max-height: 340px; overflow-y: auto; position: static; }
  .wiki-content { padding: 18px 17px 40px; }
}
/* Sized to match HmRag's own wiki page, value for value. Selected on the element rather than
   HmRag's .prod-img class, because the sanitizer strips every attribute but alt — and it should:
   an image that survives sanitising is by definition one HmRag put there, so there is nothing
   else in this article for the rule to hit. */
.wiki-article >>> img { background: #fff; border: 1px solid #e4e8ec; border-radius: 8px; float: right; height: auto; margin: 0 0 14px 22px; max-width: 34%; object-fit: contain; padding: 8px; width: 240px; }
/* HmRag clears the float on its own wrapper; here the article is the wrapper. */
.wiki-article::after { clear: both; content: ''; display: block; }
@media (max-width: 640px) {
  .wiki-article >>> img { float: none; margin: 0 0 14px; max-width: 100%; width: 100%; }
}
</style>
